import React, {useMemo} from 'react';

type Mode = 'date' | 'time' | 'datetime';

type Props = {
  value: Date;
  mode?: Mode;
  display?: string;
  is24Hour?: boolean;
  onChange?: (event: {type: string}, date?: Date) => void;
  testID?: string;
};

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function toDateValue(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toTimeValue(d: Date) {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Minimal HTML input stand-in for @react-native-community/datetimepicker.
 * Matches the (event, date) onChange signature the app already uses.
 */
export default function DateTimePicker({value, mode = 'date', onChange}: Props) {
  const inputType = mode === 'time' ? 'time' : 'date';
  const inputValue = useMemo(
    () => (mode === 'time' ? toTimeValue(value) : toDateValue(value)),
    [value, mode],
  );

  return (
    <input
      type={inputType}
      value={inputValue}
      style={{
        fontSize: 18,
        padding: 12,
        width: '100%',
        boxSizing: 'border-box',
        border: '1px solid #ccc',
        borderRadius: 8,
        direction: 'ltr',
      }}
      onChange={e => {
        const next = new Date(value);
        if (mode === 'time') {
          const [hh, mm] = e.target.value.split(':').map(Number);
          next.setHours(hh || 0, mm || 0, 0, 0);
        } else {
          const [y, m, d] = e.target.value.split('-').map(Number);
          next.setFullYear(y, (m || 1) - 1, d || 1);
        }
        onChange?.({type: 'set'}, next);
      }}
    />
  );
}
